/**
 * "Este dispositivo no está registrado" (T039; spec US3, escenario 1).
 *
 * Es el destino de `fermin:unauthenticated` y `fermin:device-mismatch` (`routes/__root.tsx`) y de
 * Inicio cuando no hay clave de dispositivo o `GET /me` respondió `401`.
 *
 * Piloto 2026-09-30: quien abre la app instalada sin estar registrado casi siempre viene de Safari
 * con un código de traspaso (iOS, R-5). Por eso la pantalla **pide el código directamente** con la
 * misma lógica que `/codigo` (`useHandoffCodeForm`: canje, errores en línea y `/registrar`), y
 * deja como opciones secundarias:
 *
 * 1. **Solicitar vinculación** → formulario de documento (`rebind-request.tsx`) → seguimiento.
 * 2. **Ver estado de mi solicitud** → `/solicitud/{ref}`, solo si ya hay una guardada en esta
 *    sesión del navegador (`rebind-store.ts`).
 *
 * Con `?reason=session-expired` (lo añade la puerta de Inicio) el caso es otro: el teléfono ya
 * estuvo vinculado y la sesión venció, así que se explica eso y la acción principal es solicitar
 * la vinculación ("Tengo un código" → `/codigo` queda como alternativa).
 */
import { KeyRound, Smartphone } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../components/ui/button';
import { Screen, ScreenLead, ScreenTitle } from '../../components/ui/screen';
import { t } from '../../i18n';
import {
  HandoffCodeField,
  HandoffCodeSubmit,
  useHandoffCodeForm,
} from '../activation/handoff-code';

import { RebindRequestForm } from './rebind-request';
import { getRebindRequest } from './rebind-store';

export interface NotRegisteredScreenProps {
  /** `true` con `?reason=session-expired`: explica la sesión vencida. */
  sessionExpired?: boolean;
  /** Código de traspaso canjeado: recibe el token de invitación rotado (igual que `/codigo`). */
  onRedeemed: (token: string) => void;
  /** "Tengo un código" → `/codigo` (solo en la variante de sesión vencida). */
  onHasCode: () => void;
  /** Tras el `202`: seguimiento de la solicitud recién enviada. */
  onRequested: (ref: string) => void;
  /** "Ver estado de mi solicitud" con la referencia guardada. */
  onViewStatus: (ref: string) => void;
}

export function NotRegisteredScreen({
  sessionExpired = false,
  onRedeemed,
  onHasCode,
  onRequested,
  onViewStatus,
}: NotRegisteredScreenProps) {
  const [showForm, setShowForm] = useState(false);
  // Se lee una vez al montar: dentro de la pantalla no cambia por sí sola.
  const [stored] = useState(() => getRebindRequest());
  const form = useHandoffCodeForm(onRedeemed);

  if (showForm) {
    return <RebindRequestForm onSubmitted={onRequested} onCancel={() => setShowForm(false)} />;
  }

  const statusButton = stored ? (
    <Button variant="secondary" onClick={() => onViewStatus(stored.ref)}>
      {t('access.notRegistered.statusAction')}
    </Button>
  ) : null;
  const help = (
    <p className="text-fm-sm text-fm-text-secondary">{t('access.notRegistered.help')}</p>
  );

  if (sessionExpired) {
    return (
      <Screen
        data-testid="access-not-registered"
        footer={
          <>
            <Button
              icon={<Smartphone aria-hidden="true" size={20} />}
              onClick={() => setShowForm(true)}
            >
              {t('access.notRegistered.requestAction')}
            </Button>
            {statusButton}
            <Button
              variant="secondary"
              icon={<KeyRound aria-hidden="true" size={20} />}
              onClick={onHasCode}
            >
              {t('access.notRegistered.codeAction')}
            </Button>
            {help}
          </>
        }
      >
        <div data-testid="session-expired" className="flex flex-col gap-fm-4">
          <ScreenTitle>{t('access.sessionExpired.title')}</ScreenTitle>
          <ScreenLead>{t('access.sessionExpired.description')}</ScreenLead>
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      data-testid="access-not-registered"
      title={t('access.notRegistered.title')}
      lead={t('access.notRegistered.lead')}
    >
      <HandoffCodeField form={form} />
      {/* El botón principal va junto a las casillas, antes de la opción secundaria (piloto 2026-09-30). */}
      <HandoffCodeSubmit form={form} />

      <section
        aria-labelledby="not-registered-no-code"
        className="mt-auto flex flex-col gap-fm-3 border-t border-fm-border pt-fm-4"
      >
        <h2 id="not-registered-no-code" className="text-fm-sm font-semibold text-fm-text-secondary">
          {t('access.notRegistered.noCodeTitle')}
        </h2>
        <Button
          variant="secondary"
          icon={<Smartphone aria-hidden="true" size={20} />}
          onClick={() => setShowForm(true)}
        >
          {t('access.notRegistered.requestAction')}
        </Button>
        {statusButton}
        {help}
      </section>
    </Screen>
  );
}
